import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Volume2, Maximize2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useVoiceChat } from "../hooks";

// Ses dalgası bileşeni
const AudioWaveform = ({ level }: { level: number }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const cleanup = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };

    if (!svgRef.current) return cleanup;

    let currentLevel = 0;
    const targetLevel = level;
    const smoothingFactor = 0.15;

    const animate = () => {
      currentLevel += (targetLevel - currentLevel) * smoothingFactor;

      // Sinüs dalgası oluştur
      const points: [number, number][] = [];
      const amplitude = Math.min(15, currentLevel * 0.3);
      const frequency = 0.05;
      const points2: [number, number][] = [];
      const amplitude2 = Math.min(10, currentLevel * 0.2);

      for (let x = 0; x <= 100; x += 2) {
        const y1 =
          20 + Math.sin(x * frequency + Date.now() * 0.005) * amplitude;
        const y2 =
          20 + Math.cos(x * frequency + Date.now() * 0.003) * amplitude2;
        points.push([x, y1]);
        points2.push([x, y2]);
      }

      // SVG path oluştur
      const path1 = `M ${points.map(([x, y]) => `${x},${y}`).join(" L ")}`;
      const path2 = `M ${points2.map(([x, y]) => `${x},${y}`).join(" L ")}`;

      const svgContent = `
        <path d="${path1}" class="stroke-green-500" fill="none" strokeWidth="1.5" />
        <path d="${path2}" class="stroke-green-500 opacity-50" fill="none" strokeWidth="1" />
      `;

      svgRef.current!.innerHTML = svgContent;

      if (Math.abs(currentLevel - targetLevel) > 0.1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return cleanup;
  }, [level]);

  return (
    <svg
      ref={svgRef}
      className="w-16 h-6 mx-1"
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
    />
  );
};

export const MiniVoiceChat: React.FC = () => {
  const { t } = useTranslation();

  // ✅ Store-based hook - NO prop drilling
  const {
    isVoiceChatActive,
    isVoiceChatMinimized,
    isListening,
    isSpeaking,
    audioLevel,
    maximizeVoiceChat,
    closeVoiceChat,
  } = useVoiceChat();

  // Eğer voice chat aktif değilse veya minimize edilmemişse gösterme
  if (!isVoiceChatActive || !isVoiceChatMinimized) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
        className="fixed bottom-4 right-4 z-50"
      >
        <div className="flex items-center gap-2 p-2 rounded-lg bg-background border border-border shadow-lg">
          {/* Voice Status Icon */}
          <div className="relative">
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center",
                isSpeaking
                  ? "bg-blue-500/20"
                  : isListening
                  ? "bg-green-500/20"
                  : "bg-yellow-500/20"
              )}
            >
              {/* Pulse Animation */}
              <div
                className={cn(
                  "absolute inset-0 rounded-full",
                  isListening
                    ? "border-2 border-green-500 animate-ping"
                    : isSpeaking
                    ? "border-2 border-blue-500 animate-pulse"
                    : "border border-yellow-500"
                )}
              ></div>

              {/* Icon */}
              {isSpeaking ? (
                <Volume2 className="h-4 w-4 text-blue-500" />
              ) : (
                <Mic
                  className={cn(
                    "h-4 w-4",
                    isListening ? "text-green-500" : "text-yellow-500"
                  )}
                />
              )}
            </div>
          </div>

          {/* Ses dalgası (isListening aktif ise) */}
          {isListening && <AudioWaveform level={audioLevel} />}

          {/* Status Text */}
          <span
            className={cn(
              "text-xs font-medium",
              isSpeaking
                ? "text-blue-500"
                : isListening
                ? "text-green-500"
                : "text-yellow-500"
            )}
          >
            {isSpeaking
              ? t("chat.aiSpeaking")
              : isListening
              ? t("chat.speakNow")
              : t("chat.processing")}
          </span>

          {/* Butonlar */}
          <div className="flex space-x-1">
            {/* Maximize Button */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={maximizeVoiceChat}
              className="h-6 w-6 rounded-full hover:bg-accent"
              title={t("common.maximize")}
            >
              <Maximize2 className="h-3 w-3" />
            </Button>

            {/* Kapat Butonu */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={closeVoiceChat}
              className="h-6 w-6 rounded-full hover:bg-accent hover:text-red-500 hover:bg-red-500/20"
              title={t("common.close")}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
